<script setup lang="ts">
import type { CampsiteMediaResponse } from '@/types/api/search';
import type { MediaType, SubType, ModifiedCampsiteMediaStructured, ModifiedCampsiteMediaImageOrVideo, CampsiteMediaImage, CampsiteMediaVideo } from '@/types/media';

import { mediaIsImage, mediaIsModifiedImage, mediaIsModifiedVideo } from '@/types/guards/media';

// icons
import { faArrowUp, faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { faCube, faSearchPlus } from '@fortawesome/pro-light-svg-icons';

import { RBN_CAMPSITE_DETAIL } from '@/utils/constants';

// components
import CiCampsiteMediaImage from '@/components/CiCampsiteMedia/CiCampsiteMediaImage.vue';
import CiCampsiteMediaPanono from '@/components/CiCampsiteMedia/CiCampsiteMediaPanono.vue';
import CiCampsiteMediaVideo from '@/components/CiCampsiteMedia/CiCampsiteMediaVideo.vue';
import CiSkeletonItem from '@/components/skeleton/CiSkeletonItem.vue';
import CiStickyNavigation from '@/components/CiStickyNavigation/CiStickyNavigation.vue';

import type { StickyNavigationItem } from '@/components/CiStickyNavigation/CiStickyNavigation.vue';

type Links = { campsite: number[]; visitor: number[] };
type MediaData = {
  campsite: CampsiteMediaResponse;
  mediaStructured: ModifiedCampsiteMediaStructured;
  links: Links;
  tempStickyNavigationItems: (StickyNavigationItem<unknown> & { label: string[] })[];
};

definePageMeta({
  layout: 'default',
});

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const route = useRoute();
const searchService = useSearchService();
const campsiteStore = useCampsiteStore();
const seoStore = useSeoStore();
const { $gettext, $pgettext } = useGettext();
const { fetchError, fetchFailed, fetchStatus } = useFetchError();
const { locale } = useI18n();
const { requestAds } = useAds();

const { ppcWebsiteLink, scrollToRef, sectionRefs } = useCampsiteDetail({
  campsite: () => data.value?.campsite,
});

const { hasAds, isPartOfAvailabilityService, getPreEnterData } = useCampsiteAdsAs({
  campsite: () => data.value?.campsite,
});

/********************
 * REFS & VARS       *
 ********************/
const dividerTexts = reactive({
  picture: {
    campsite: '',
    visitor: '',
  },
  video: {
    campsite: '',
    visitor: '',
  },
});
const freezeBottomNavbar = ref(false);
const lastScrollPosition = ref(0);
const links = ref<Links | null>(null);
const showBottomNavbar = ref(false);
const subNavigationAnchors = ref<StickyNavigationItem<{ label: { text: string; count: number } }>[]>([]);

const media = ref<ModifiedCampsiteMediaImageOrVideo[]>([]);
const mediaRefs = new Map();
const mediaStructured = ref<ModifiedCampsiteMediaStructured>({
  campsite: [],
  visitor: [],
});

/********************
 * INITIALIZATION    *
 ********************/
if (import.meta.client) {
  await useAsyncData(route.fullPath, getPreEnterData);
}
const { data, pending, error } = await useLazyAsyncData(`media-${route.params.identifier}`, async (): Promise<MediaData> => {
  try {
    const response = await searchService.getCampsiteMedia(route.params?.identifier as string, locale.value);
    const dividers: any = {};
    const tempStickyNavigationItems: (StickyNavigationItem<unknown> & { label: string[] })[] = [];
    let firstTwoImages: number = 0;

    campsiteStore.campsiteFromMedia = response;

    const hasPpcOrAs
      = (response.purchases ? response.purchases.includes(256) : false) || [
        'ctoutvert',
        'compusoft',
        'camping.info-bvcd',
        'camping.info-booking',
        'camping.care',
        'feratel',
        'changer',
        'phobs',
      ].includes(response.booking_provider);

    /*
      1. campsite pics
      2. panono
      3. campsite vids
      4. visitor pics
      5. visitor vids
    */
    type OrderKeys = 'picture-campsite' | 'panono' | 'video-campsite' | 'picture-visitor' | 'video-visitor';
    const orders: Record<OrderKeys, number> = {
      'picture-campsite': 0,
      'panono': 1,
      'video-campsite': 2,
      'picture-visitor': 3,
      'video-visitor': 4,
    };

    const mediaCopy = JSON.parse(JSON.stringify(response.media));
    mediaCopy.sort((a: any, b: any) => {
      const subtypeA = a.media_type === 'panono' ? '' : `-${a.picture_type || a.video_type}`;
      const subtypeB = b.media_type === 'panono' ? '' : `-${b.picture_type || b.video_type}`;
      const orderKeyA = `${a.media_type}${subtypeA}` as OrderKeys;
      const orderKeyB = `${b.media_type}${subtypeB}` as OrderKeys;
      const orderA: number = orders[orderKeyA];
      const orderB: number = orders[orderKeyB];

      if (orderA < orderB) {
        return -1;
      } else if (orderA > orderB) {
        return 1;
      }
      return 0;
    });

    mediaCopy.map((item: any, idx: number): ModifiedCampsiteMediaImageOrVideo[] => {
      const mediaType = item.media_type;
      const pictureType = item.picture_type;
      const videoType = item.video_type;
      const dividerType = `${mediaType}-${pictureType}-${videoType}`;

      if (mediaType === 'picture') {
        firstTwoImages += 1;
      }

      item.idx = idx;
      item.eager = firstTwoImages < 3;

      if (!dividers[dividerType]) {
        dividers[dividerType] = true;

        if (mediaType !== 'panono') {
          const subType = pictureType || videoType || 'campsite';

          if (idx % 2 === 0) {
            idx++;
          }

          tempStickyNavigationItems.push({
            label: [mediaType, subType],
          });
        }
      }
      item.selected = false;
      return item;
    });

    const mediaStructured: ModifiedCampsiteMediaStructured = mediaCopy.reduce(
      (acc: { campsite: ModifiedCampsiteMediaImageOrVideo[]; visitor: ModifiedCampsiteMediaImageOrVideo[] }, item: ModifiedCampsiteMediaImageOrVideo) => {
        if (item.media_type === 'panono') {
          acc.campsite.push(item);
        } else if (mediaIsModifiedImage(item)) {
          if (item.picture_type === 'campsite') {
            acc.campsite.push(item);
          } else if (item.picture_type === 'visitor') {
            acc.visitor.push(item);
          }
        } else if (mediaIsModifiedVideo(item)) {
          if (item.video_type === 'campsite') {
            acc.campsite.push(item);
          } else if (item.video_type === 'visitor') {
            acc.visitor.push(item);
          }
        }
        return acc;
      },
      { campsite: [], visitor: [] },
    );

    seoStore.setDynamicMetaParam({
      campsite_name: response.name,
    });

    return {
      campsite: response,
      mediaStructured,
      links: {
        campsite: hasPpcOrAs ? getLinksForMedia(mediaStructured['campsite'].length) : [],
        visitor: hasPpcOrAs ? getLinksForMedia(mediaStructured['visitor'].length) : [],
      },
      tempStickyNavigationItems,
    };
  } catch (error) {
    fetchError(error);
    throw error;
  }
});

if (error.value) {
  throw createError({ statusCode: error.value?.statusCode, message: error.value.statusMessage });
}

onMounted(() => {
  window.addEventListener('scroll', onScroll);
  window.addEventListener('keyup', onKeyUp);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('keyup', onKeyUp);
});

const { onKeyUp, onMediaItemClicked } = useCampsiteMedia({
  media: () => media.value,
  mediaElements: mediaRefs,
});

/********************
 * FUNCTIONS         *
 ********************/
watch(() => data.value, setData, { immediate: true });

function setData(newValue: MediaData | undefined) {
  if (newValue) {
    mediaStructured.value = newValue.mediaStructured;
    media.value = [...newValue.mediaStructured.campsite, ...newValue.mediaStructured.visitor];
    links.value = newValue.links;

    setTimeout(() => {
      initStickyNavigation();
    }, 100);

    nextTick(() => {
      // refresh ad slots
      requestAds();
    });
  }
}

function initStickyNavigation() {
  const getReduceFunction = (mediaType: MediaType, subType: SubType | null) => {
    return (accumulator: number, currentValue: CampsiteMediaImage | CampsiteMediaVideo) => {
      let n = 0;
      const objSubType = mediaIsImage(currentValue) ? currentValue.picture_type : currentValue.video_type || null;
      if (currentValue.media_type === mediaType && objSubType === subType) {
        n = 1;
      }
      return accumulator + n;
    };
  };

  const numItems = (mediaType: MediaType, subType: SubType | null) => data.value?.campsite.media.reduce(getReduceFunction(mediaType, subType), 0) || 0;
  const counts = {
    picture: {
      campsite: numItems('picture', 'campsite') + numItems('panono', null),
      visitor: numItems('picture', 'visitor'),
    },
    video: {
      campsite: numItems('video', 'campsite'),
      visitor: numItems('video', 'visitor'),
    },
  };

  const getNavigationText = (mediaType: MediaType, subType: SubType): { text: string; count: number } => {
    const texts: Record<Exclude<MediaType, 'panono'>, Record<SubType, string>> = {
      picture: {
        campsite: $pgettext('Media Sticky navigation - keep this short!', 'Campsite owner photos'),
        visitor: $pgettext('Media Sticky navigation - keep this short!', 'Guests photos'),
      },
      video: {
        campsite: $pgettext('Media Sticky navigation - keep this short!', 'Campsite owner videos'),
        visitor: $pgettext('Media Sticky navigation - keep this short!', 'Guests videos'),
      },
    };
    if (mediaType !== 'panono' && texts[mediaType]) {
      let count = 0;
      if (counts[mediaType]) {
        count = counts[mediaType][subType] || 0;
      }
      return {
        text: texts[mediaType][subType],
        count,
      };
    }
    return {
      text: '',
      count: 0,
    };
  };

  if (!data.value) {
    return;
  }

  subNavigationAnchors.value = data.value.tempStickyNavigationItems.map((item) => {
    const [mediaType, subType] = item.label as [MediaType, SubType];
    const label = getNavigationText(mediaType, subType);

    if (mediaType !== 'panono') {
      dividerTexts[mediaType][subType] = label.text;
    }

    return {
      label,
      ref: sectionRefs[`${subType}-${mediaType}`] as HTMLElement,
    };
  });
}

function onScroll() {
  // Get the current scroll position
  const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
  // Because of momentum scrolling on mobiles, we shouldn't continue if it is less than zero
  if (currentScrollPosition < 0) {
    return;
  }
  let scrolledToBottomOffset = 50;
  const footer = document.getElementById('footer');
  if (footer) {
    scrolledToBottomOffset = footer.clientHeight;
  }
  freezeBottomNavbar.value = false;
  if (Math.abs(currentScrollPosition + window.innerHeight - document.body.offsetHeight) <= scrolledToBottomOffset) {
    showBottomNavbar.value = true;
    freezeBottomNavbar.value = true;
    lastScrollPosition.value = currentScrollPosition;
    return;
  }
  if (Math.abs(currentScrollPosition - lastScrollPosition.value) < 60) {
    return;
  }
  // Here we determine whether we need to show or hide the navbar
  showBottomNavbar.value = currentScrollPosition > 150 && currentScrollPosition < lastScrollPosition.value;
  // Set the current scroll position as the last scroll position
  lastScrollPosition.value = currentScrollPosition;
}

function getMediaItemClasses(item: ModifiedCampsiteMediaImageOrVideo) {
  return [item.selected ? 'selected w-full mb-[10px]' : 'w-full lg:w-[calc(50%-5px)] group mb-[10px]', `media-item--${item.media_type}`];
}

function onScrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function getAdId(idx: number) {
  const mapping: Record<number, string> = {
    9: 'camping_content_3',
    13: 'camping_content_4',
    17: 'camping_content_5',
  };
  return mapping[idx] || '';
}

function getLinksForMedia(numMedia: number) {
  let links: any[] = [];
  if (numMedia < 3) {
    links = [numMedia];
  } else {
    links = [4];
    const sample = 1;
    let maxNum = Math.max(1, Math.floor(numMedia / sample));
    let minNum = Math.max(Math.floor(maxNum / 2), 1);
    const numPpc = Math.floor(Math.random() * (maxNum - minNum + 1) + minNum);

    let lastPpcOrAs = 2;
    for (let i = 0; i < numPpc; i++) {
      minNum = lastPpcOrAs + 2;
      maxNum = Math.min(numMedia, minNum + i * Math.floor(numMedia / numPpc));
      lastPpcOrAs
        = Math.min(
          Math.floor(numMedia / 2),
          Math.floor(
            Math.random() * (Math.floor(maxNum / 2) - Math.floor(minNum / 2) + 1) + Math.floor(minNum / 2),
          ),
        ) * 2;
      if (!links.includes(lastPpcOrAs) && !links.includes(lastPpcOrAs - 1) && !links.includes(lastPpcOrAs - 2)) {
        links.push(lastPpcOrAs);
      }
    }
  }
  return links;
}
</script>

<template>
  <div>
    <template v-if="!fetchFailed">
      <template v-if="pending">
        <CiSkeletonItem
          color="rgb(var(--color-gray-10))"
          class="h-[114px] border-b-2 border-gray-40"
        />

        <template v-if="hasAds">
          <div class="absolute size-full">
            <div class="container h-full">
              <div class="flex h-full">
                <CiSkeletonItem
                  class="ad ad--skyscraper ad--skyscraper-left sticky top-0 z-10"
                  style="top: 50px"
                />
                <CiSkeletonItem
                  class="ad ad--skyscraper ad--skyscraper-right sticky top-0 z-10"
                  style="top: 50px"
                />
              </div>
            </div>
          </div>

          <div class="container-lg mb-4 mt-2 pt-0">
            <CiSkeletonItem class="ad ad--header" />
          </div>
        </template>

        <div class="container mt-4 flex flex-wrap gap-x-[10px]">
          <template
            v-for="idx in 8"
            :key="`item_${idx}`"
          >
            <div class="media-item mb-[10px] w-full lg:w-[calc(50%-5px)]">
              <CiSkeletonItem class="relative h-full rounded pt-[66.61%]" />
            </div>

            <template v-if="hasAds && idx === 4">
              <div
                :key="`ad_${idx}`"
                class="w-full"
              >
                <CiSkeletonItem class="ad--h-600-480-570 mb-[10px] w-full" />
              </div>
            </template>
          </template>
        </div>
      </template>
      <template v-else-if="!pending && data">
        <!-- back navigation -->
        <div class="bg-gray-10">
          <div class="container py-2">
            <CiNavigationToggle
              type="black"
              :href="localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: route.params.identifier } })"
            >
              <span class="ml-3 text-dark">{{ $gettext('Back to %{campsite}', { campsite: data.campsite.name }, true) }}</span>
            </CiNavigationToggle>
          </div>
        </div>

        <!-- sticky navigation -->
        <CiStickyNavigation
          :ref="el => (sectionRefs['subnav'] = el as ComponentPublicInstance<typeof CiStickyNavigation>)"
          :items="subNavigationAnchors"
          :jump-to-first="false"
          :jump-to-last="true"
          @item-clicked="scrollToRef"
        >
          <template #item="{ label }">
            <span class="flex items-center">
              {{ label.text }}
              <span class="ml-1 inline-block rounded-[10rem] bg-dark-green px-[7px] py-[3px] text-xs font-bold leading-3 text-white">{{ label.count }}</span>
            </span>
          </template>
        </CiStickyNavigation>

        <div class="relative">
          <div
            v-if="hasAds"
            class="skyscraper absolute size-full pb-12"
          >
            <div class="container h-full">
              <div class="flex h-full">
                <CiAdSlot
                  id="camping_sidebar_left"
                  class="ad--skyscraper ad--skyscraper-left sticky top-0"
                  style="top: 50px; z-index: 31"
                />
                <CiAdSlot
                  id="camping_sidebar_1"
                  class="ad--skyscraper ad--skyscraper-right sticky top-0"
                  style="top: 50px; z-index: 31"
                />
                <!-- won't be rendered -->
                <!-- <ci-ad-slot id="cpdp_wallpaper_pixel"></ci-ad-slot> -->
              </div>
            </div>
          </div>

          <div
            v-if="hasAds"
            class="container-lg mb-4 mt-2 pt-0"
          >
            <CiAdSlot
              id="camping_header"
              class="ad--header"
            />
          </div>

          <!-- media elements -->
          <div
            class="container relative mt-4 flex flex-wrap gap-x-[10px]"
            :class="{ 'mb-32 md:mb-20': !freezeBottomNavbar, 'mb-4': freezeBottomNavbar }"
          >
            <div
              v-if="mediaStructured['campsite'].some((item) => (item.media_type === 'picture'))"
              id="campsite-picture"
              :ref="el => (sectionRefs['campsite-picture'] = el as HTMLElement)"
              class="divider invisible m-0 size-0 w-full p-0"
            />
            <template v-for="(item, idx) of mediaStructured['campsite']" :key="item.id">
              <template v-if="hasAds && (item.idx + 1) === 5">
                <div
                  :key="`ad_${item.idx}`"
                  class="mb-[10px] w-full"
                >
                  <CiAdSlot
                    id="camping_content_2"
                    class="ad--h-600-480-570"
                  />
                </div>
              </template>
              <template v-if="hasAds && ((item.idx + 1) === 9 || (item.idx + 1) === 13 || (item.idx + 1) === 17)">
                <div
                  :key="`ad_${item.idx + 1}`"
                  class="mb-[10px] w-full"
                >
                  <CiAdSlot
                    :id="getAdId(item.idx + 1)"
                    class="ad--h-250"
                  />
                </div>
              </template>

              <div
                v-if="
                  item.idx !== undefined
                    && item.idx !== null
                    && data?.links.campsite?.includes(idx)
                    && (ppcWebsiteLink('inline') || isPartOfAvailabilityService)
                "
                :key="`link-${item.idx}`"
                class="website-link mb-6 mt-3 w-full text-center"
              >
                <div class="flex flex-col justify-center lg:flex-row">
                  <CiButton
                    v-if="ppcWebsiteLink('inline')"
                    tag="NuxtLink"
                    :variant="isPartOfAvailabilityService ? 'secondary' : 'primary'"
                    :href="ppcWebsiteLink('inline')"
                    class="w-full md:mr-2 md:w-auto"
                    referrerpolicy="unsafe-url"
                    target="_blank"
                    rel="noopener"
                  >
                    <span>{{ $gettext('Go to website') }}</span>
                    <CiAwesomeIcon
                      :icon="faChevronRight"
                      ratio="0.6"
                    />
                  </CiButton>
                  <CiButton
                    v-if="isPartOfAvailabilityService"
                    tag="NuxtLink"
                    class="mt-2 lg:mt-0"
                    :href="
                      localePath({
                        name: RBN_CAMPSITE_DETAIL,
                        params: { identifier: route.params.identifier },
                        hash: '#availabilities',
                      })
                    "
                  >
                    {{ $gettext('Check availability') }}
                    <CiAwesomeIcon
                      class="ml-2"
                      :icon="faChevronRight"
                      ratio="0.6"
                    />
                  </CiButton>
                </div>
              </div>

              <div
                v-if="mediaStructured['campsite'].findIndex((item) => (item.media_type === 'video')) === idx"
                id="campsite-video"
                :ref="el => (sectionRefs['campsite-video'] = el as HTMLElement)"
                class="divider invisible m-0 size-0 p-0"
                :class="{ 'w-full': idx % 2 === 0 }"
              />

              <div
                :ref="el => (mediaRefs.set(item.idx, el as HTMLElement))"
                :data-testid="`campsite-media-${idx}`"
                class="media-item"
                :class="getMediaItemClasses(item)"
              >
                <CiCampsiteMediaImage
                  v-if="item.media_type === 'picture'"
                  :item="item"
                  :selected="item.selected"
                  :alt-fallback="data.campsite.name"
                  @click="onMediaItemClicked($event, item, 50)"
                />

                <CiCampsiteMediaPanono
                  v-else-if="item.media_type === 'panono'"
                  :selected="item.selected"
                  :panono-id="item.id"
                  @click="onMediaItemClicked($event, item, 50)"
                />

                <CiCampsiteMediaVideo
                  v-else-if="item.media_type === 'video'"
                  :item="item"
                  :selected="item.selected"
                  @click="onMediaItemClicked($event, item, 50)"
                />

                <CiAwesomeIcon
                  v-if="item.media_type !== 'video'"
                  :icon="faSearchPlus"
                  ratio="2.0"
                  class="zoom-icon absolute fill-white"
                />

                <div
                  v-if="item.media_type === 'panono'"
                  class="panono-icon absolute fill-white"
                >
                  <CiAwesomeIcon
                    :icon="faCube"
                    ratio="2.0"
                  />
                </div>
              </div>
            </template>
            <div
              v-if="mediaStructured['visitor'].some((item) => (item.media_type === 'picture'))"
              id="visitor-picture"
              :ref="el => (sectionRefs['visitor-picture'] = el as HTMLElement)"
              class="divider w-full"
            >
              <h2
                class="my-4 text-center"
              >
                {{ $pgettext('Media Sticky navigation - keep this short!', 'Guests photos') }}
              </h2>
            </div>
            <template v-for="(item, idx) of mediaStructured['visitor']" :key="item.id">
              <template v-if="hasAds && (item.idx + 1) === 5">
                <div
                  :key="`ad_${item.idx}`"
                  class="mb-[10px] w-full"
                >
                  <CiAdSlot
                    id="camping_content_2"
                    class="ad--h-600-480-570"
                  />
                </div>
              </template>

              <template v-if="hasAds && ((item.idx + 1) === 9 || (item.idx + 1) === 13 || (item.idx + 1) === 17)">
                <div
                  :key="`ad_${item.idx + 1}`"
                  class="mb-[10px] w-full"
                >
                  <CiAdSlot
                    :id="getAdId(item.idx + 1)"
                    class="ad--h-250"
                  />
                </div>
              </template>
              <div
                v-if="
                  item.idx !== undefined
                    && item.idx !== null
                    && links['visitor']?.includes(idx)
                    && (ppcWebsiteLink('inline') || isPartOfAvailabilityService)
                "
                :key="`link-${item.idx}`"
                class="website-link mb-6 mt-3 w-full text-center"
              >
                <div class="flex flex-col justify-center lg:flex-row">
                  <CiButton
                    v-if="ppcWebsiteLink('inline')"
                    tag="NuxtLink"
                    :variant="isPartOfAvailabilityService ? 'secondary' : 'primary'"
                    :href="ppcWebsiteLink('inline')"
                    class="w-full md:w-auto"
                    referrerpolicy="unsafe-url"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $gettext('Go to website') }}
                    <CiAwesomeIcon
                      clas-s="ml-2"
                      :icon="faChevronRight"
                      ratio="0.6"
                    />
                  </CiButton>
                  <CiButton
                    v-if="isPartOfAvailabilityService"
                    tag="NuxtLink"
                    class="mt-2 lg:mt-0"
                    :href="
                      localePath({
                        name: RBN_CAMPSITE_DETAIL,
                        params: { identifier: route.params.identifier },
                        hash: '#availabilities',
                      })
                    "
                  >
                    {{ $gettext('Check availability') }}
                    <CiAwesomeIcon
                      class="ml-2"
                      :icon="faChevronRight"
                      ratio="0.6"
                    />
                  </CiButton>
                </div>
              </div>

              <div
                v-if="mediaStructured['visitor'].findIndex((item) => (item.media_type === 'video')) === idx"
                id="visitor-video"
                :ref="el => (sectionRefs['visitor-video'] = el as HTMLElement)"
                class="divider invisible m-0 size-0 p-0"
                :class="{ 'w-full': idx % 2 === 0 }"
              />

              <div
                :ref="el => (mediaRefs.set(item.idx, el as HTMLElement))"
                :data-testid="`visitor-media-${idx}`"
                class="media-item"
                :class="getMediaItemClasses(item)"
              >
                <CiCampsiteMediaImage
                  v-if="item.media_type === 'picture'"
                  :item="item"
                  :selected="item.selected"
                  :alt-fallback="data.campsite.name"
                  @click="onMediaItemClicked($event, item, 50)"
                />

                <CiCampsiteMediaPanono
                  v-else-if="item.media_type === 'panono'"
                  :selected="item.selected"
                  :panono-id="item.id"
                  @click="onMediaItemClicked($event, item, 50)"
                />

                <CiCampsiteMediaVideo
                  v-else-if="item.media_type === 'video'"
                  :item="item"
                  :selected="item.selected"
                  @click="onMediaItemClicked($event, item, 50)"
                />

                <CiAwesomeIcon
                  v-if="item.media_type !== 'video'"
                  :icon="faSearchPlus"
                  ratio="2.0"
                  class="zoom-icon absolute fill-white"
                />

                <div
                  v-if="item.media_type === 'panono'"
                  class="panono-icon absolute fill-white"
                >
                  <CiAwesomeIcon
                    :icon="faCube"
                    ratio="2.0"
                  />
                </div>
              </div>
            </template>
          </div>

          <!-- bottom nav bar -->
          <transition name="fade">
            <div
              v-if="showBottomNavbar"
              class="navbar-bottom bottom-[15px] left-0 z-10 flex w-full flex-wrap justify-center px-2 md:flex-nowrap"
              :class="{ 'fixed': !freezeBottomNavbar, 'mb-6': freezeBottomNavbar }"
            >
              <CiButton
                tag="NuxtLink"
                variant="secondary"
                :href="localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: route.params.identifier } })"
                class="mb-2 w-full md:mb-0 md:mr-2 md:w-auto"
              >
                <CiAwesomeIcon
                  class="mr-2"
                  :icon="faChevronLeft"
                  ratio="0.6"
                />
                {{ $gettext('Back to campsite') }}
              </CiButton>
              <CiButton
                tag="NuxtLink"
                variant="secondary"
                href="#"
                class="w-full md:w-auto"
                referrerpolicy="unsafe-url"
                target="_blank"
                rel="noopener"
                @click.prevent="onScrollToTop"
              >
                {{ $gettext('Back to top') }}
                <CiAwesomeIcon
                  class="ml-2"
                  :icon="faArrowUp"
                  ratio="0.6"
                />
              </CiButton>
            </div>
          </transition>
        </div>
      </template>
    </template>
    <CiErrorView
      v-else
      :error="fetchStatus"
    />
  </div>
</template>

<style lang="postcss" scoped>
.media-item {
  transition: filter 0.5s ease-out;

  .zoom-icon,
  .panono-icon {
    @apply pointer-events-none left-1/2 top-1/2 opacity-0 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-500 ease-out;
  }

  .panono-icon {
    @apply rounded-xl bg-black/70 p-5 opacity-0;
  }

  &:not(.selected) {
    .panono-icon {
      @apply opacity-80;
    }
  }

  .no-touch &:hover {
    @apply cursor-pointer;

    .panono-icon {
      @apply opacity-0;
    }

    &:not(.selected) {
      filter: brightness(70%);

      .zoom-icon {
        @apply opacity-100;
      }
    }
  }
}

@media (hover: none) and (pointer: coarse) {
  .media-item .panono-icon {
    display: none;
  }
}
</style>
